import { Country } from "@/api/base"
import {
  EventRule,
  EventRuleCarrierDimension,
  EventRuleCarrierOption,
  EventRuleDestinationValue,
  EventRuleDimensionOptions,
  EventRuleLocationOption,
  EventRuleStatusOption,
} from "@/api/integration/type"
import {
  ShipmentStalledRule,
} from "@/pages/Integration/Events/components/ShipmentStalledRulesTable/types"
import {
  ShipmentTransitDelayRule,
} from "@/pages/Integration/Events/components/ShipmentTransitDelayRulesTable/types"
import { OrderStatus, SubStatusData } from "@/pages/Orders/OrderStatusTypes"

/**
 * API <-> UI 视图模型映射层
 *
 * 后端返回的是「维度 code」（承运商 code、Location gid、状态 code、目的地国家码），
 * 页面上的表格 / 弹窗使用的是可读的视图模型。这里集中做双向转换，
 * 让 store 只持有原始 API 数据、组件只消费视图模型。
 */

/** thresholds(number[]) <-> days(["2d","4d"]) */
export const thresholdsToDays = (thresholds: number[] = []): string[] =>
  thresholds.map((n) => `${n}d`)

export const daysToThresholds = (days: string[] = []): number[] =>
  days
    .map((d) => Number(String(d).replace(/d$/i, "")
      .trim()))
    .filter((n) => !Number.isNaN(n))

/** 承运商 code -> { name, logo } */
const resolveCarriers = (
  values: string[] | undefined,
  carrierOptions: EventRuleCarrierOption[] = [],
): { name: string; logo?: string }[] => {
  if (!values?.length) {
    return []
  }

  const byCode = new Map(carrierOptions.map((c) => [c.code, c]))

  return values.map((code) => {
    const option = byCode.get(code)

    return { name: option?.name ?? code, logo: option?.logo }
  })
}

/**
 * 是否「全选承运商」：后端 all 标记为真，或已选数量等于全部可选承运商数量。
 * 全选时物流商可达上千个，表格改用「All carriers」标签展示，避免逐一列出。
 */
const isAllCarriers = (
  carriers: EventRuleCarrierDimension | undefined,
  options: EventRuleCarrierOption[] = [],
): boolean => {
  if (!carriers) {
    return false
  }

  if (carriers.all) {
    return true
  }

  const count = carriers.values?.length ?? 0

  return count > 0 && count === options.length
}

/** 发货地点 id -> 名称 */
const resolveLocationNames = (
  values: string[] | undefined,
  locationOptions: EventRuleLocationOption[] = [],
): string[] => {
  if (!values?.length) {
    return []
  }

  const byId = new Map(locationOptions.map((l) => [l.id, l]))

  return values.map((id) => byId.get(id)?.name ?? id)
}

/**
 * 目的地 { code, provinces } -> 可读文本
 * 国家码用 commonStore.countries 解析成国家名称展示；有省份时附带省份数量。
 */
const resolveDestinationLabels = (
  values: EventRuleDestinationValue[] | undefined,
  countries: Country[] = [],
): string[] => {
  if (!values?.length) {
    return []
  }

  const byCode = new Map(countries.map((c) => [c.code, c]))

  return values.map((item) => {
    const name = byCode.get(item.code)?.name ?? item.code

    return item.provinces?.length ? `${name} (${item.provinces.length})` : name
  })
}

/** API EventRule -> 运输延迟表格视图模型 */
export const mapTransitDelayRule = (
  rule: EventRule,
  options: EventRuleDimensionOptions = {},
  countries: Country[] = [],
): ShipmentTransitDelayRule => ({
  id: rule.rule_id,
  type: rule.is_default ? "fallback" : "custom",
  carrierCodes: rule.dimensions?.carriers?.values ?? [],
  locationIds: rule.dimensions?.locations?.values ?? [],
  destinationValues: rule.dimensions?.destinations?.values ?? [],
  carriers: resolveCarriers(
    rule.dimensions?.carriers?.values,
    options.carriers,
  ),
  allCarriers: isAllCarriers(rule.dimensions?.carriers, options.carriers),
  locations: resolveLocationNames(rule.dimensions?.locations?.values, options.locations),
  destinations: resolveDestinationLabels(rule.dimensions?.destinations?.values, countries),
  days: thresholdsToDays(rule.thresholds),
  secondaryAction: rule.is_default ? "disable" : "delete",
  isDisabled: !rule.enabled,
})

/**
 * 运输停滞状态维度：把 API 的 statuses 选项转成状态树，
 * 供 StatusTreeSelect / 表格展示复用。
 * - 有子状态：父节点 id = `st-<status>`，子节点 id = `<code>`
 * - 无子状态：叶子节点 id = `<status>`
 */
export interface StalledStatusTreeNode {
  id: string
  name: string
  /** 带 namespace 的语言包 key；缺失时用 name（接口 label）兜底。见 resolveStatusNodeName */
  i18nKey?: string
  children?: { id: string; name: string; i18nKey?: string }[]
}

/**
 * 状态节点显示名的翻译函数，由组件层传入。
 *
 * 不在这里直接调 i18next：状态树是 MobX computed，而 i18next 不是 observable，
 * 在树里就把名字翻译好的话，切语言不会触发重算，表格里会留着上一个语言的状态名。
 */
export type StatusLabelTranslator = (i18nKey: string, fallback: string) => string

/** 有 key 就翻译，没有（后端没下发 / 子状态 code 不认识）就用接口 label */
export const resolveStatusNodeName = (
  node: { name: string; i18nKey?: string },
  translate: StatusLabelTranslator,
): string => (node.i18nKey ? translate(node.i18nKey, node.name) : node.name)

// 子状态 code -> 语言包 key。接口只给一级状态的 i18n_key，子状态查前端这张表
const SUB_STATUS_I18N_KEYS = new Map<number, string>(
  Object.values(SubStatusData).map((item) => [item.status, item.il8nKey]),
)

/** 一级状态的 key 在 common、子状态的在 orders；两处都只给了裸 key，这里补 namespace */
const withNamespace = (key: string | undefined, ns: string) =>
  key === undefined ? undefined : key.includes(":") ? key : `${ns}:${key}`

// 不属于「运输停滞」的状态，从状态选项里排除（含其子状态）：
// - Pending：包裹尚未进入运输环节，谈不上停滞
// - Delivered：已送达
const STALLED_EXCLUDED_STATUSES = new Set<number>([
  OrderStatus.Pending,
  OrderStatus.Delivered,
])

// 子状态 code 的千位即父状态（如 4001/4004 属于 Delivered=4）
const parentStatusOf = (subCode: number) => Math.floor(subCode / 1000)

export const buildStalledStatusTree = (
  statuses: EventRuleStatusOption[] = [],
): StalledStatusTreeNode[] =>
  statuses
    .filter((status) => !STALLED_EXCLUDED_STATUSES.has(status.status))
    .map((status) => {
      const i18nKey = withNamespace(status.i18n_key, "common")

      // 原本没有子状态：叶子节点，id 用状态本身
      if (!status.substatuses?.length) {
        return { id: String(status.status), name: status.label, i18nKey }
      }

      const children = status.substatuses
        .filter((sub) => !STALLED_EXCLUDED_STATUSES.has(parentStatusOf(sub.code)))
        .map((sub) => ({
          id: String(sub.code),
          name: sub.label,
          i18nKey: withNamespace(SUB_STATUS_I18N_KEYS.get(sub.code), "orders"),
        }))

      // 仅有一个子状态（等同于父状态本身，如 Info received / Ready for pickup / Expired）：
      // 折叠成叶子，不显示子集与折叠图标；但 id 仍用子状态 code（传给后端的是子状态）。
      // 展示的是父级 label，所以 i18n key 也取父级的
      if (children.length === 1) {
        return { id: children[0].id, name: status.label, i18nKey }
      }

      // 多个子状态：父 + 子；若子状态全被排除则 children 为空，后续过滤掉
      return { id: `st-${status.status}`, name: status.label, i18nKey, children }
    })
    // 过滤后子状态为空的父节点也一并去掉
    .filter((node) => !node.children || node.children.length > 0)

/** 停滞规则 dimensions.statuses.values(number[]) -> 视图 statusIds(string[]) */
const statusValuesToIds = (values: number[] | undefined): string[] =>
  (values ?? []).map((code) => String(code))

/** API EventRule -> 运输停滞表格视图模型 */
export const mapStalledRule = (
  rule: EventRule,
  options: EventRuleDimensionOptions = {},
): ShipmentStalledRule => ({
  id: rule.rule_id,
  type: rule.is_default ? "fallback" : "custom",
  statusIds: statusValuesToIds(rule.dimensions?.statuses?.values),
  carrierCodes: rule.dimensions?.carriers?.values ?? [],
  carriers: resolveCarriers(rule.dimensions?.carriers?.values, options.carriers),
  allCarriers: isAllCarriers(rule.dimensions?.carriers, options.carriers),
  days: thresholdsToDays(rule.thresholds),
  secondaryAction: rule.is_default ? "disable" : "delete",
  isDisabled: !rule.enabled,
})

/**
 * 通用状态标签格式化：给定选中的叶子 id + 状态树，输出可读文本数组。
 * 状态树不再写死，由维度选项 buildStalledStatusTree 构建后传入。
 */
export const formatStatusLabelsByTree = (
  statusIds: string[],
  tree: StalledStatusTreeNode[],
  subStatusCountFormat: (selected: number, total: number) => string,
  translate: StatusLabelTranslator,
): string[] => {
  const labels: string[] = []
  const selectedSet = new Set(statusIds)

  tree.forEach((status) => {
    const statusName = resolveStatusNodeName(status, translate)

    if (!status.children || status.children.length === 0) {
      if (selectedSet.has(status.id)) {
        labels.push(statusName)
      }

      return
    }

    const selectedChildren = status.children.filter((c) => selectedSet.has(c.id))

    if (selectedChildren.length === 0) {
      return
    }

    if (selectedChildren.length === status.children.length) {
      labels.push(statusName)
      return
    }

    labels.push(
      `${statusName} (${subStatusCountFormat(selectedChildren.length, status.children.length)})`,
    )
  })

  return labels
}
